import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';

import {
  HANDLING_GROUPS,
  HANDLING_MODES,
  REVALIDATE_MODES,
} from '@savgroup-front-common/constants';
import { computeOrderDetailsProductWeight } from '@savgroup-front-common/core/api/Carrier/getPickupPointByGeoCoordQuery/helpers/computeOrderDetailsProductWeight';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import {
  AddressInfoDto,
  OrderDetailsProduct,
  PickupPointAddress,
} from '@savgroup-front-common/types';
import { ClaimService } from '@/api';
import { useGetClaimGroupCarrierQuery } from '@/view/app/hooks/useGetClaimGroupCarrierQuery';
import { useGetClaimGroupConfirmation } from '@/view/app/hooks/useGetClaimGroupConfirmation';
import { useGetHandlingByClaimGroup } from '@/view/app/hooks/useGetHandlingByClaimGroup';
import useGetHomePickup from '@/view/app/hooks/useGetHomePickup';
import useGetMultiCarrierProductByIdQuery from '@/view/app/hooks/useGetMultiCarrierProductByIdQuery';

import { getGroupedCarriers } from '../../../../../helpers';
import { IrshStepValues } from '../IrshPages.types';

import ClaimGroupDeliveryPageSchema from './ClaimGroupDeliveryPage.schema';
import { ClaimGroupDeliveryValues } from './ClaimGroupDeliveryPage.types';
import useSuggestedAddress from './hooks/useSuggestedAddress';

const useClaimGroupDeliveryPage = ({
  onNextStep,
}: {
  onNextStep: RoutedStepProps<IrshStepValues>['onNextStep'];
}) => {
  const { pushErrors } = useToasts();

  const { values } = useRoutedStepsOrchestratorContext<IrshStepValues>();

  const { homePickupAddress, mail } = useGetHomePickup();
  const { claimGroupId } = useParams<{ claimGroupId: string }>();
  const depositAddress = values?.handlingDeposit?.depositAddress;
  const ownerAddress = depositAddress || homePickupAddress;
  const { claimGroupConfirmation, isLoading: isGetClaimGroupConfirmation } =
    useGetClaimGroupConfirmation({
      claimGroupId,
    });

  const { handling, isLoading: isGetHandlingLoading } =
    useGetHandlingByClaimGroup({ claimGroupId });
  const { carriers, isLoading: isGetCarrierLoading } =
    useGetClaimGroupCarrierQuery({
      claimGroupId,
      handlingMode: HANDLING_MODES.DELIVERY,
    });

  const { multiCarrierProductSummary } = useGetMultiCarrierProductByIdQuery({
    carrierProductIds: carriers
      ?.map((carrier) => carrier.carrierProductId)
      .filter((carrier) => carrier) as string[],
  });

  const { groupedCarriers } = getGroupedCarriers({
    carriers,
    multiCarrierProductSummary,
    handlingInfo: handling,
  });

  const shouldShowTransportDeduction =
    handling?.canDeduceTransportFromRefund &&
    carriers?.some((carrier) => carrier.priceWithTax);

  const formContext = useForm<ClaimGroupDeliveryValues>({
    defaultValues: ownerAddress
      ? {
          addressSelected: {
            ...ownerAddress,
            firstName: ownerAddress.firstname,
            lastName: ownerAddress.lastname,
            mail,
          },
        }
      : {},
    resolver: yupResolver(ClaimGroupDeliveryPageSchema),
    mode: REVALIDATE_MODES.ON_CHANGE,
  });

  const { handleSubmit, setValue, watch } = formContext;

  const changeSelection = () => {
    setValue('chooseHandling', undefined);
  };

  const {
    mutateAsync: handleDeliveryHandling,
    isLoading: isLoadingDeliveryHandlingSubmit,
  } = useMutation(
    ['setDeliveryHandling'],
    async ({
      claimGroupId,
      payload,
    }: {
      claimGroupId?: string;
      payload: {
        handlingMode: HANDLING_MODES;
        pickupPointId?: string;
        pickupPointAddress?: PickupPointAddress | null;
        carrierCustomerPriceId?: string;
        carrierSellerPriceId?: string;
        address?: AddressInfoDto | null;
      };
    }) => {
      const responseHandling = await ClaimService.setClaimGroupHandling({
        claimGroupId,
        payload,
      });

      if (responseHandling.failure) {
        pushErrors(responseHandling.errors);

        return undefined;
      }

      return responseHandling;
    },
  );

  const { handleAddressSubmit } = useSuggestedAddress({
    formContext,
  });

  const chooseHandlingSelected = watch('chooseHandling');
  const handlingSelected = Boolean(
    chooseHandlingSelected === HANDLING_GROUPS.PICKUP_POINT ||
    chooseHandlingSelected === HANDLING_GROUPS.PICKUP_STORE ||
    chooseHandlingSelected === HANDLING_GROUPS.PICKUP_STORE_DELIVERY,
  );

  const homeSelectedAddressChanged = Boolean(
    chooseHandlingSelected === HANDLING_GROUPS.HOME ||
    chooseHandlingSelected === HANDLING_GROUPS.HOME_INTERVENTION ||
    chooseHandlingSelected === HANDLING_GROUPS.EXTERNAL,
  );

  const onSubmit = handleSubmit(
    async ({
      chooseHandling,
      pickupPointSelected,
      addressSelected,
      addressSuggested,
      keepSuggestedAddress,
    }) => {
      Promise.resolve(handleAddressSubmit({ addressSelected }))
        .then((shouldBeChoiceAnAddress) => {
          const payload = pickupPointSelected
            ? {
                handlingMode: HANDLING_MODES.DELIVERY,
                pickupPointId: pickupPointSelected.id,
                pickupPointAddress: pickupPointSelected && {
                  id: pickupPointSelected.id,
                  name: pickupPointSelected.name,
                  firstName: pickupPointSelected.name,
                  address: pickupPointSelected.adress,
                  additionalAddress: pickupPointSelected.adress3,
                  postalCode: pickupPointSelected.postalCode,
                  city: pickupPointSelected.city,
                  countryCode: pickupPointSelected.countryCode,
                },
                carrierCustomerPriceId:
                  pickupPointSelected.carrierCustomerPriceId,
                carrierSellerPriceId: pickupPointSelected.carrierSellerPriceId,
                address: undefined,
              }
            : {
                handlingMode: HANDLING_MODES.DELIVERY,
                pickupPointId: undefined,
                pickupPointAddress: null,
                carrierCustomerPriceId:
                  groupedCarriers[chooseHandling || HANDLING_GROUPS.HOME][0]
                    .carrierCustomerPriceId,
                carrierSellerPriceId:
                  groupedCarriers[chooseHandling || HANDLING_GROUPS.HOME][0]
                    .carrierSellerPriceId,
                address: keepSuggestedAddress
                  ? addressSuggested
                  : addressSelected,
              };

          return {
            shouldBeChoiceAnAddress,
            payload,
          };
        })
        .then(async ({ payload }) => {
          const responseHandling = await handleDeliveryHandling({
            claimGroupId,
            payload,
          });

          if (!responseHandling) {
            return undefined;
          }

          onNextStep({
            newValues: {
              handlingDelivery: {
                chooseHandling,
                pickupPointSelected,
                depositAddress: keepSuggestedAddress
                  ? addressSuggested
                  : addressSelected,
              },
            },
          });
        });
    },
  );

  const isDeliveryHandlingLoading =
    isGetCarrierLoading || isGetHandlingLoading || isGetClaimGroupConfirmation;

  const products = values.claims?.reduce<OrderDetailsProduct[]>(
    (acc, claim) => {
      const orderProduct = values.order?.products.find(
        (product) => product.ownerProductId === claim.ownerProductId,
      );

      if (orderProduct) {
        return [...acc, orderProduct];
      }

      return acc;
    },
    [],
  );
  const weight = computeOrderDetailsProductWeight({
    products,
  });

  return {
    claimGroupConfirmation,
    groupedCarriers,
    shouldShowTransportDeduction,
    formContext,
    changeSelection,
    onSubmit,
    isDeliveryHandlingLoading,
    isLoadingSubmit: isLoadingDeliveryHandlingSubmit,
    chooseHandlingSelected,
    handlingSelected,
    homeSelectedAddressChanged,
    weight,
  };
};

export default useClaimGroupDeliveryPage;
